import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { Field, useFormikContext } from 'formik';

import { createStyles } from '../../../utils/createStyle';

import {
    FormikComboField,
    FormikCurrencyField,
    FormikIncrementField,
    FormikTextField,
} from './utils/fields';
import { sortOptionValuesAscendingWithDisabled } from './utils';

import { Product, ProductVariant } from 'common/api/e-comm/models/Product';
import { monetize, demonetize, monetizeWithDecimal } from 'common/utils/strings';
import useUnavailableProductVariants from './utils/useUnavailableProductVariants';
import { ProductInfoBonusExpiration } from './ProductInfoBonusExpiration';

const styles = createStyles({
    fieldContainer: ['w-full', { maxWidth: '14rem' }],
    container: [
        'block lg:grid mt-4',
        {
            gridTemplateColumns: 'auto auto',
        },
    ],
    ctrlIncrement: [
        'inline-block',
        {
            maxWidth: '140px',
        },
    ],
    incrementAndTotal: [
        'grid mt-4 lg:mt-0',
        {
            gridTemplateColumns: 'auto auto',
        },
    ],
    listShipToHome: [
        'mb-8 mt-2 text-xs text-grey-1',
        {
            listStyleType: 'disc',
            paddingLeft: '20px',
        },
    ],
    listComboContainer: ['w-full', { maxWidth: '220px' }],
    row: ['grid gap-4', { height: 'auto' }],
    mainField: [
        'sm:grid gap-3',
        {
            maxWidth: '12rem',
        },
    ],
    fieldValue: ['mb-5 sm:mt-0', { width: '12rem' }],
    fieldQuantity: ['', { maxWidth: '9rem' }],
    fieldEarning: [
        'w-auto h-12 max-h-14 bg-grey-7 mb-1 sm:mb-5 px-2 mt-5 sm:mt-0 text-brand rounded-sm flex flex-col justify-between',
        { maxHeight: '3.15rem', maxWidth: '12rem' },
    ],
});

export interface ProductAmountInputProps {
    product: Product;
    label?: ReactNode;
    hideQuantityField?: boolean;
    hideTotal?: boolean;
    customLabel?: (label: string) => ReactNode;
    uiSettings?: UiSettings;
    cardBalance?: number | null;
    setVariant?: Function;
}

export interface UiSettings {
    totalMargin?: string;
}

export interface ProductAmountInputFormData {
    cardId?: string;
    priceOverride: number | null;
    selectedOptions: { [key: string]: string };
    quantity: number;
    hideAddedToCartAnimation?: boolean;
}

const calculateTotal = (
    product: Product,
    data: ProductAmountInputFormData,
    showQuantity: boolean
) => {
    const hasOptions = product.options && product.options.length;
    const quantity = !showQuantity ? 1 : data.quantity;

    if (hasOptions) {
        const options = Object.values(data.selectedOptions).map(demonetize);
        return _.sum(options) * quantity;
    }

    return (data?.priceOverride || 0) * quantity;
};

const defaultUiSettings: UiSettings = {
    totalMargin: 'mb-0 mt-auto sm:mb-auto sm:mt-0',
};

export const ProductAmountInput = ({
    product,
    hideQuantityField,
    label,
    hideTotal,
    customLabel,
    uiSettings,
    cardBalance,
    setVariant,
}: ProductAmountInputProps) => {
    const { totalMargin: totalVerticalMargin } = uiSettings || defaultUiSettings;
    const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>();

    const formCtx = useFormikContext<ProductAmountInputFormData>();

    const showQuantity = !hideQuantityField;
    const total = calculateTotal(product, formCtx.values, showQuantity);

    const { variants } = useUnavailableProductVariants(product);

    const gridTemplateColumns = `11.5rem ${showQuantity ? '9rem' : '0rem'} ${
        selectedVariant?.bonus ? 'max-content max-content' : 'max-content'
    }`;
    const gridTemplateColumnsContainer = `max-content ${!hideTotal ? 'auto' : ''}`;

    const productMinValue = product.minValue;
    const productMaxValue = product.maxValue;
    const productValueIncrement = product.unitPrice;
    const productMaxBalance = product.maxBalance;

    const customValidator = (value: number) => {
        if (productMaxBalance && cardBalance && value + cardBalance > productMaxBalance) {
            return `Enter an amount so your total card balance is $${productMaxBalance} or less.`;
        } else if (productMinValue && value < productMinValue) {
            return `Value must be greater than $${productMinValue}`;
        } else if (productMaxValue && value > productMaxValue) {
            return `Value must be less than $${productMaxValue}`;
        }

        return undefined;
    };

    const getQuantityFieldLabel = useCallback(
        () => (customLabel ? customLabel('Quantity') : 'Quantity'),
        [customLabel]
    );

    const renderTotal = () => {
        return (
            <>
                {!hideTotal && (
                    <div className={`text-right flex flex-col ${totalVerticalMargin} ml-auto`}>
                        <div className="text-sm text-brand-dark ml-auto mt-auto">Total</div>
                        <div className="text-2xl text-brand-dark font-bold">
                            {monetizeWithDecimal(total)}
                        </div>
                    </div>
                )}
            </>
        );
    };

    useEffect(() => {
        const value = Object.values(formCtx.values.selectedOptions)[0];
        const realValue = value ? demonetize(value) : value;
        setSelectedVariant(
            product.variants.find((prd) => prd.price === realValue) || product.variants[0]
        );
        if (setVariant) {
            setVariant(
                product.variants.find((prd) => prd.price === realValue) || product.variants[0]
            );
        }
    }, [product, formCtx.values.selectedOptions, setVariant]);

    return (
        <div
            className={`pdp_amount_qty_earnings ${styles.row}`}
            style={{ gridTemplateColumns: gridTemplateColumnsContainer }}
        >
            <div className={styles.mainField} style={{ gridTemplateColumns }}>
                <div className={styles.fieldValue}>
                    {product.canOverridePrice && (
                        <Field
                            component={FormikCurrencyField}
                            label={label}
                            name="priceOverride"
                            min={productMinValue}
                            max={productMaxValue}
                            increment={productValueIncrement}
                            customValidator={customValidator}
                            validate={customValidator}
                        />
                    )}
                    {!product.canOverridePrice && !product.options.length && (
                        <div>
                            <Field
                                className="o:text-brand text-xl font-normal pt-0"
                                hideFieldDescription
                                component={FormikTextField}
                                label={label}
                                name="priceOverride"
                                value={monetize(Number(formCtx.values.priceOverride))}
                                disabled
                            />
                        </div>
                    )}
                    {product.options.map((option) => (
                        <div key={option.id}>
                            <Field
                                component={FormikComboField}
                                key={option.id}
                                name={`selectedOptions[${option.id}]`}
                                label={customLabel ? customLabel(option.label) : option.label}
                                options={sortOptionValuesAscendingWithDisabled(
                                    option.values,
                                    variants
                                )}
                                controlClassName="pr-2 text-lg"
                            />
                        </div>
                    ))}
                </div>
                <div className={styles.fieldQuantity}>
                    {showQuantity && (
                        <Field
                            component={FormikIncrementField}
                            name="quantity"
                            label={getQuantityFieldLabel()}
                            increment={1}
                            min={1}
                            max={9999}
                            hideFieldDescription={true}
                        />
                    )}
                </div>
                <div className="flex">
                    <div
                        className={`pdp_earnings_field ${styles.fieldEarning} ${
                            selectedVariant?.bonus ? 'text-brand-accent' : 'text-brand'
                        }`}
                    >
                        <span className="text-xs font-bold">Earnings</span>
                        <p className="text-xl">
                            <span className="text-grey-3 line-through">
                                {!selectedVariant?.bonus ? '' : `${selectedVariant.rebate}%`}
                            </span>{' '}
                            {selectedVariant?.bonus?.rebate
                                ? `${selectedVariant?.bonus?.rebate}%`
                                : `${selectedVariant?.rebate}%`}
                        </p>
                    </div>
                    <ProductInfoBonusExpiration
                        expiration={selectedVariant?.bonus?.expiration}
                        showQuantity={showQuantity}
                    />
                </div>
            </div>
            {renderTotal()}
        </div>
    );
};
